// One bounded retry for transient public-feed failures. Never cache error pages
// or accept a response for a different season, category, date, or match.
export function validateApiPayload(url, payload) {
  const parsed = new URL(String(url), 'https://volleywire.invalid');
  const name = parsed.pathname.split('/').at(-1);
  const arrayFields = { scores: 'games', teams: 'teams', rankings: 'rankings',
    standings: 'conferences', leaders: 'leaders', upcoming: 'groups',
    'team-schedule': 'games', 'team-season-stats': 'entries' };
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  if (arrayFields[name] && !Array.isArray(payload[arrayFields[name]])) return false;
  if (name === 'game-flow' && (String(payload.gameId) !== parsed.searchParams.get('gameId') ||
    !payload.payloads || typeof payload.payloads !== 'object' || Array.isArray(payload.payloads))) return false;
  const season = parsed.searchParams.get('season');
  if (season && ['teams', 'rankings', 'team-schedule', 'team-season-stats'].includes(name) && Number(payload.season) !== Number(season)) return false;
  if (name === 'scores' && payload.date && payload.date !== parsed.searchParams.get('date')) return false;
  if (name === 'leaders' && (payload.type !== parsed.searchParams.get('type') ||
    payload.category?.key !== parsed.searchParams.get('category'))) return false;
  return true;
}

export async function fetchApiJson(url, options = {}) {
  const { signal, timeoutMs = 20000, ...init } = options;
  const controller = new AbortController();
  const cancel = () => controller.abort(signal?.reason);
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  const timer = setTimeout(() => controller.abort(new DOMException('The request timed out. Please retry.', 'TimeoutError')), timeoutMs);
  try {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        controller.signal.throwIfAborted();
        const response = await fetch(url, { ...init, signal: controller.signal });
        let payload = null;
        try { payload = await response.json(); } catch (error) {
          if (controller.signal.aborted) throw error;
        }
        controller.signal.throwIfAborted();
        if ([502, 503, 504].includes(response.status) && attempt === 0) continue;
        if (response.ok && !validateApiPayload(url, payload)) throw new Error('The data service returned an invalid response. Please retry.');
        return { response, payload };
      } catch (error) {
        if (controller.signal.aborted) throw controller.signal.reason;
        if (attempt === 1) throw error;
      }
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

// Detach one cancelled consumer without aborting a shared request.
export function waitForApiRequest(task, signal) {
  if (!signal) return task;
  if (signal.aborted) return Promise.reject(signal.reason || new DOMException('Cancelled', 'AbortError'));
  return new Promise((resolve, reject) => {
    const cancel = () => reject(signal.reason || new DOMException('Cancelled', 'AbortError'));
    signal.addEventListener('abort', cancel, { once: true });
    task.then(resolve, reject).finally(() => signal.removeEventListener('abort', cancel));
  });
}
